import { JSONSchema7WithErrors, ResourceDetail } from '@cibo/core'
import { CiboSensedLegend, FormikWithWarningsProvider } from '@cibo/ui'
import Info from '@mui/icons-material/Info'
import { Alert, Box, Button, CircularProgress, Stack, Tooltip } from '@mui/material'
import { Form, FormikHelpers, FormikProps } from 'formik'
import { useSnackbar } from 'notistack'
import { ReactNode, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { useBatchedField } from '../../queries'
import { DetailModalState } from './DetailEditingProvider/DetailEditingContext'
import {
  GenericDetailEditor,
  GenericEditorSkeleton,
  detailFromGenericEditorValues,
} from './GenericDetailEditor'
import { TraitFeature } from './TraitFeaturesById'
import { useSaveDetail } from './queries/useSaveDetail'

type EditSingleFieldProps = {
  requestClose(): void
  simple?: boolean
  DeleteButton: ReactNode
  onDirtyChange?: (isDirty: boolean) => void
} & DetailModalState

const DirtyReporter = ({
  dirty,
  onDirtyChange,
}: {
  dirty: boolean
  onDirtyChange?: (isDirty: boolean) => void
}) => {
  useEffect(() => {
    onDirtyChange?.(dirty)
  }, [dirty])
  return null
}

export const EditSingleFieldFormWithoutModal = ({
  filterInputs,
  onSuccess,
  requestClose,
  requirement,
  resourceId,
  DeleteButton,
  traitId,
  onDirtyChange,
}: EditSingleFieldProps) => {
  const { t } = useTranslation('@cibo/landmanager/DetailEditing')
  const { enqueueSnackbar } = useSnackbar()

  const field = useBatchedField(resourceId)
  const fieldModels = useMemo(() => field.data && [field.data], [field.dataUpdatedAt])

  const saveDetail = useSaveDetail()
  const year = requirement?.year

  useEffect(() => {
    if (resourceId) {
      saveDetail.reset()
    }
  }, [resourceId])

  const Trait = traitId ? TraitFeature.forTraitId(traitId) : undefined
  const { t: traitT } = useTranslation(Trait?.ns)

  const handleSubmit = (
    // source is provided with most/many values but accepted on none!
    { source, ...values }: ResourceDetail,
    formikHelpers: FormikHelpers<any>
  ) => {
    if (!resourceId || !traitId) {
      return
    }

    const detailValue = !!Trait
      ? Trait?.detailFromEditorValues
        ? Trait.detailFromEditorValues(values, requirement)
        : values
      : detailFromGenericEditorValues(values, { traitId, year, dataType: 'field' })

    saveDetail
      .mutateAsync({
        resourceId,
        details: [detailValue],
      })
      .then(() => {
        requestClose()
        enqueueSnackbar(t('updateSuccess', { trait: t(traitId, { ns: 'traits' }) }), {
          variant: 'success',
        })
        onSuccess && onSuccess()
        formikHelpers.resetForm()
      })
      .finally(() => {
        formikHelpers.setSubmitting(false)
      })
  }

  const initialValue = useMemo(
    () => !!traitId && field.data?.findDetail({ traitId, year }),
    [year, field.isPending, traitId]
  )

  const initialValueSource = useMemo(
    () => !!traitId && field.data?.findDetail({ traitId, year })?.source,
    [year, field.isPending, traitId]
  )

  const initialValues = useMemo(
    () =>
      field.isPending
        ? ({} as ResourceDetail)
        : ({
            input: Trait?.initialValues
              ? Trait.initialValues({ fieldModels: field.data ? [field.data] : [], year })
              : ({} as ResourceDetail),
            traitId,
            source: initialValueSource,
            year,
          } as ResourceDetail),
    [year, field.isPending, traitId, initialValueSource]
  )

  const validationSchema = useMemo(
    () =>
      Trait?.validationSchema
        ? Yup.object().shape({
            input: Trait.validationSchema({ requirement, t: traitT }),
          })
        : undefined,
    [Trait, requirement, traitT]
  )

  const DetailEditor = !!Trait ? Trait.simpleEditor ?? Trait.editor : GenericDetailEditor

  const editorValidJsonSchema: JSONSchema7WithErrors | undefined = useMemo(
    () =>
      Trait?.validJsonSchema
        ? {
            type: 'object',
            properties: { input: Trait.validJsonSchema({ requirement, t: traitT }) },
          }
        : undefined,
    [Trait, requirement, traitT]
  )
  const editorWarningJsonSchema: JSONSchema7WithErrors | undefined = useMemo(
    () =>
      Trait?.warningJsonSchema
        ? {
            type: 'object',
            properties: { input: Trait.warningJsonSchema({ requirement, t: traitT }) },
          }
        : undefined,
    [Trait, requirement, traitT]
  )

  return field.isFetching ? (
    <CircularProgress />
  ) : (
    <>
      <FormikWithWarningsProvider
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        validJsonSchema={editorValidJsonSchema}
        warningJsonSchema={editorWarningJsonSchema}
        enableReinitialize
      >
        {({ dirty, isValid, submitForm }: FormikProps<any>) => {
          return (
            <Form>
              <DirtyReporter dirty={dirty} onDirtyChange={onDirtyChange} />
              <Stack>
                <CiboSensedLegend traitDetails={[initialValues]} />
                <Stack spacing={1} p={2} pb={0}>
                  {field.isPending ? (
                    <GenericEditorSkeleton />
                  ) : !!traitId ? (
                    <DetailEditor
                      traitId={traitId}
                      name={'input'}
                      fieldModels={fieldModels || []}
                      //@ts-ignore this component may accept a year, but typing isn't apparently handling that conditionality
                      year={year}
                      requirement={requirement}
                      filterInputs={filterInputs}
                    />
                  ) : null}
                </Stack>
                <Stack sx={{ background: 'background.paper' }} spacing={2} p={2} pt={0}>
                  <Box flex={1}>
                    {saveDetail.isError && (
                      <Alert severity="error">
                        <Stack alignItems="center" direction="row">
                          {t('problemSaving')}
                          <Tooltip placement="top" title={saveDetail.error.response?.data.message}>
                            <Info sx={{ height: 18, width: 18, marginLeft: 1 }} />
                          </Tooltip>
                        </Stack>
                      </Alert>
                    )}
                  </Box>
                  <Button
                    style={{ width: '100%' }}
                    variant="contained"
                    onClick={submitForm}
                    disabled={(!dirty && initialValueSource !== 'cibo') || !isValid}
                    loading={saveDetail.isPending}
                    data-testid="save-detail-button"
                  >
                    {initialValue ? t('savePractice') : t('addPractice')}
                  </Button>
                  {DeleteButton}
                </Stack>
              </Stack>
            </Form>
          )
        }}
      </FormikWithWarningsProvider>
    </>
  )
}
